import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import {
  PROJECT_OPEN_ITEM_LIST_LIMIT,
  type ProjectOpenItem,
  type ProjectOpenItemGroup,
  type ProjectOpenItemGroupKey,
} from '@avisoma/shared/dist/api-schema/paths/api-projects-id-open-items.api.schema.js';
import { Card } from '../../components/Card';
import { ID } from '../../components/ID';
import { Link } from '../../components/Link';
import { Skeleton } from '../../components/Skeleton';
import { Switch, SwitchField } from '../../catalyst/switch';
import { Label } from '../../catalyst/fieldset';
import { usePersistedState } from '../../hooks/usePersistetState';
import { formatNumber } from '../../lib/numberFormat';
import backend from '../../services/backend';
import {
  OPEN_ITEM_GROUP_LABELS,
  groupsForIncludeErrors,
  openItemHref,
  shouldShowOpenItemsSection,
  visibleOpenItemGroups,
} from './openItems';

const INCLUDE_ERRORS_KEY = 'projectView.openItems.includeErrors';

interface OpenItemsProps {
  projectId: string;
}

export function OpenItems({ projectId }: OpenItemsProps) {
  const [includeErrors, setIncludeErrors] = usePersistedState(INCLUDE_ERRORS_KEY, true);
  const [expandedGroup, setExpandedGroup] = useState<ProjectOpenItemGroupKey | null>(null);

  const countsQuery = useQuery({
    queryKey: ['getProjectOpenItems', projectId],
    queryFn: () => backend.getProjectOpenItems(projectId).then((res) => res.data),
    enabled: !!projectId,
    refetchInterval: 10_000,
  });

  const visibleGroups = visibleOpenItemGroups(
    groupsForIncludeErrors(countsQuery.data?.groups ?? [], includeErrors),
  );
  const groupStillVisible =
    expandedGroup != null && visibleGroups.some((group) => group.key === expandedGroup);
  if (expandedGroup != null && countsQuery.data && !groupStillVisible) {
    setExpandedGroup(null);
  }

  const activeGroup = groupStillVisible ? expandedGroup : null;

  const itemsQuery = useQuery({
    queryKey: ['getProjectOpenItems', projectId, activeGroup],
    queryFn: () => {
      if (activeGroup == null) {
        throw new Error('Open items group is required');
      }
      return backend.getProjectOpenItems(projectId, { group: activeGroup }).then((res) => res.data);
    },
    enabled: !!projectId && activeGroup != null,
    refetchInterval: activeGroup != null ? 10_000 : false,
  });

  const header = (
    <div className="flex items-center justify-between gap-4">
      <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Open items</h2>
      <SwitchField className="shrink-0">
        <Label className="text-sm whitespace-nowrap">Include errors</Label>
        <Switch
          checked={includeErrors}
          onChange={(value) => {
            setIncludeErrors(value);
            setExpandedGroup(null);
          }}
          aria-label="Include errors"
        />
      </SwitchField>
    </div>
  );

  if (countsQuery.isError) {
    return (
      <Card header={header}>
        <p className="text-sm text-red-600 dark:text-red-400">{(countsQuery.error as Error).message}</p>
      </Card>
    );
  }

  if (
    !shouldShowOpenItemsSection({
      hasCounts: countsQuery.data != null,
      visibleCount: visibleGroups.length,
      includeErrors,
    })
  ) {
    return null;
  }

  if (visibleGroups.length === 0) {
    return (
      <Card header={header}>
        <p className="text-sm text-gray-500 dark:text-zinc-400">No pending items.</p>
      </Card>
    );
  }

  const expandedPayload =
    activeGroup != null
      ? itemsQuery.data?.groups.find((group) => group.key === activeGroup)
      : undefined;

  return (
    <Card header={header}>
      <div className="overflow-hidden rounded-md border border-gray-200 dark:border-zinc-700">
        {visibleGroups.map((group) => {
          const expanded = group.key === activeGroup;
          return (
            <OpenItemGroup
              key={group.key}
              group={group}
              expanded={expanded}
              onToggle={() => setExpandedGroup((current) => (current === group.key ? null : group.key))}
              items={expanded ? expandedPayload?.items : undefined}
              itemsLoading={expanded && itemsQuery.isLoading}
              itemsError={expanded && itemsQuery.isError ? (itemsQuery.error as Error) : null}
              itemsCount={expanded ? expandedPayload?.count : undefined}
            />
          );
        })}
      </div>
    </Card>
  );
}

function OpenItemGroup({
  group,
  expanded,
  onToggle,
  items,
  itemsLoading,
  itemsError,
  itemsCount,
}: {
  group: ProjectOpenItemGroup;
  expanded: boolean;
  onToggle: () => void;
  items: ProjectOpenItem[] | undefined;
  itemsLoading: boolean;
  itemsError: Error | null;
  itemsCount: number | undefined;
}) {
  const isErrorGroup = group.key.endsWith('-error');

  return (
    <div className="border-b border-gray-200 last:border-b-0 dark:border-zinc-700">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={clsx(
          'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 dark:hover:bg-zinc-700/50',
          expanded && 'border-b border-gray-200 dark:border-zinc-700',
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          {expanded ? (
            <ChevronDownIcon className="size-3.5 shrink-0 text-gray-500 dark:text-zinc-500" aria-hidden />
          ) : (
            <ChevronRightIcon className="size-3.5 shrink-0 text-gray-500 dark:text-zinc-500" aria-hidden />
          )}
          <span className="font-medium text-gray-900 dark:text-zinc-100">
            {OPEN_ITEM_GROUP_LABELS[group.key]}
          </span>
        </span>
        <span
          className={clsx(
            'shrink-0 tabular-nums font-medium',
            isErrorGroup ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-zinc-400',
          )}
        >
          {formatNumber(group.count)}
        </span>
      </button>
      {expanded && (
        <div className="max-h-64 overflow-y-auto px-3 py-2">
          <OpenItemGroupBody
            items={items}
            itemsLoading={itemsLoading}
            itemsError={itemsError}
            totalCount={itemsCount ?? group.count}
          />
        </div>
      )}
    </div>
  );
}

function OpenItemGroupBody({
  items,
  itemsLoading,
  itemsError,
  totalCount,
}: {
  items: ProjectOpenItem[] | undefined;
  itemsLoading: boolean;
  itemsError: Error | null;
  totalCount: number;
}) {
  if (itemsLoading) {
    return <Skeleton lines={3} />;
  }

  if (itemsError) {
    return <p className="text-sm text-red-600 dark:text-red-400">{itemsError.message}</p>;
  }

  if (!items || items.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">This group has no items.</p>;
  }

  const truncated = totalCount > items.length;

  return (
    <ul className="flex flex-col gap-0.5">
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={openItemHref(item.entityType, item.id)}
            title={item.id}
            className="flex min-w-0 items-center gap-3 rounded-md px-1.5 py-1 text-sm hover:bg-gray-50 hover:no-underline dark:hover:bg-zinc-700/50"
          >
            <span className="shrink-0 font-mono text-xs">
              <ID id={item.id} />
            </span>
            {item.label ? (
              <span className="min-w-0 truncate text-gray-700 dark:text-zinc-300" title={item.label}>
                {item.label}
              </span>
            ) : null}
            <span className="ml-auto shrink-0 text-xs text-gray-500 dark:text-zinc-400">{item.status}</span>
          </Link>
        </li>
      ))}
      {truncated ? (
        <li className="px-1.5 pt-1 text-xs text-gray-500 dark:text-zinc-400">
          Showing {formatNumber(items.length)} of {formatNumber(totalCount)}. Limit is{' '}
          {formatNumber(PROJECT_OPEN_ITEM_LIST_LIMIT)}.
        </li>
      ) : null}
    </ul>
  );
}
